/**
 * Profile photo (selfie) uploader for onboarding.
 *
 * Mirrors IDDocumentsUpload's pattern: writes a File into form.image,
 * which the onboard onSubmit detects and bundles into the multipart
 * PATCH. Doubles as the user's profile photo (rendered in navbar +
 * account page) and as the "real person" check on the admin KYC view.
 *
 * Unlike ID images, the profile photo is NOT locked after verification
 * — people change their look (haircut, weight loss, age). Admin uses
 * the ID photos as the immutable identity anchor.
 */

import { useState } from "react";
import { Avatar, Button, Modal, Upload } from "antd";
import {
  CameraOutline,
  CloudUploadOutline,
  EyeOutline,
  PersonOutline,
} from "@raresail/react-ionicons";
import { useAuth } from "../context/AuthContext";
import { resolveMediaUrl } from "../../constants/config";

interface Props {
  form: any;
  updateFormValues: (v: any) => void;
}

export const ProfilePhotoUpload = ({ form, updateFormValues }: Props) => {
  const { user } = useAuth();
  const [preview, setPreview] = useState<string | null>(null);

  const stagedFile = form?.image instanceof File ? (form.image as File) : null;
  const hasStored = !!user?.image;

  // Prefer the freshly-staged File (object URL) over what the server has.
  const displayUrl: string | null = stagedFile
    ? URL.createObjectURL(stagedFile)
    : hasStored
    ? resolveMediaUrl(user!.image as unknown as string)
    : null;

  const initials =
    `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase() ||
    (user?.email?.[0] || "?").toUpperCase();

  return (
    <div className="flexColumn gap10">
      <div className="font12 grey" style={{ lineHeight: 1.5 }}>
        Add a clear, well-lit photo of your face. We use it as your
        profile picture and to confirm you match the ID you uploaded.
      </div>

      <div className="flexNullCenter gap15">
        <Avatar
          size={88}
          src={displayUrl || undefined}
          icon={!displayUrl ? <PersonOutline fontSize={40} /> : undefined}
          style={{
            background: displayUrl ? "transparent" : "#e2e8f0",
            color: "#475569",
            cursor: displayUrl ? "pointer" : "default",
            border: "2px solid #f1f5f9",
          }}
          onClick={() => displayUrl && setPreview(displayUrl)}
        >
          {!displayUrl ? initials : null}
        </Avatar>

        <div className="flexColumn gap5 flexGrow">
          <Upload
            beforeUpload={(file) => {
              updateFormValues({ image: file });
              return false; // we send via PATCH ourselves
            }}
            onRemove={() => updateFormValues({ image: null })}
            accept="image/*"
            maxCount={1}
            showUploadList={false}
          >
            <Button icon={<CameraOutline />} block size="large">
              {stagedFile
                ? `Selected: ${stagedFile.name}`
                : hasStored
                ? "Change photo"
                : "Upload photo"}
            </Button>
          </Upload>
          {displayUrl && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutline />}
              onClick={() => setPreview(displayUrl)}
              style={{ alignSelf: "flex-start", padding: 0 }}
            >
              Preview
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        title="Profile photo"
        footer={null}
        width={420}
        destroyOnClose
      >
        {preview && (
          <img
            src={preview}
            alt="Profile"
            style={{
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 8,
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default ProfilePhotoUpload;
